// Resource Import Dispatcher - Orchestrate ordered import execution

import {
  IMPORT_ORDER,
  IMPORT_ACTION,
  CONFLICT_RESOLUTION,
  RESOURCE_STATE
} from './importConstants.js';

import {
  createHoursOfOperation,
  createAgentStatus,
  createQueue,
  createQuickConnect,
  createRoutingProfile,
  createUserHierarchyGroup,
  updateHoursOfOperation,
  updateAgentStatus,
  updateQueueOutboundCallerConfig,
  updateQuickConnectConfig,
  updateRoutingProfileQueues,
  updateUserHierarchyStructure
} from './connectCreateClient.js';

import {
  mapHoursOfOperationForCreate,
  mapAgentStatusForCreate,
  mapQueueForCreate,
  mapQuickConnectForCreate,
  mapRoutingProfileForCreate,
  buildUserHierarchyStructure
} from './reverseDataMappers.js';

// Import the SINGLETON instance (named export), not the class (default export): isCircuitBreakerError
// is an instance method, so `import requestQueueManager from ...` (the class) makes it undefined and
// every circuit-breaker check throws a TypeError that aborts the whole resource type under throttling.
import { requestQueueManager } from '../aws/requestQueueManager.js';

// Maximum circuit breaker attempts before skipping remaining resources of a type
const MAX_CIRCUIT_BREAKER_ATTEMPTS = 5;

/**
 * Import resources in dependency order
 */
export const importResources = async ({
  parsedData,
  selectedResourceTypes,
  conflictResolution,
  perResourceConflicts = {},
  existingResources,
  nameToIdMaps,
  credentials,
  region,
  instanceId,
  onProgress,
  onLog
}) => {
  const results = {
    success: [],
    failed: [],
    skipped: []
  };

  // Process resources in dependency order
  for (const resourceType of IMPORT_ORDER) {
    // Skip if not selected for import
    if (!selectedResourceTypes[resourceType]) {
      continue;
    }

    // Get resources to import
    const resources = parsedData[resourceType] || [];
    if (resources.length === 0) {
      continue;
    }

    // Report progress - starting resource type
    if (onProgress) {
      onProgress({
        resourceType,
        state: RESOURCE_STATE.PROCESSING,
        current: 0,
        total: resources.length,
        message: `Processing ${resourceType}...`
      });
    }

    // Process resources based on type
    try {
      const typeResults = await processResourceType({
        resourceType,
        resources,
        hierarchyStructure: parsedData.hierarchyStructure, // For userHierarchies
        conflictResolution,
        perResourceConflicts,
        existingResources,
        nameToIdMaps,
        credentials,
        region,
        instanceId,
        onProgress,
        onLog
      });

      results.success.push(...typeResults.success);
      results.failed.push(...typeResults.failed);
      results.skipped.push(...typeResults.skipped);

      // Report progress - completed resource type
      if (onProgress) {
        onProgress({
          resourceType,
          state: RESOURCE_STATE.SUCCESS,
          current: resources.length,
          total: resources.length,
          message: `Completed ${resourceType}`
        });
      }

    } catch (error) {
      console.error(`Error processing ${resourceType}:`, error);

      // Report progress - failed resource type
      if (onProgress) {
        onProgress({
          resourceType,
          state: RESOURCE_STATE.FAILED,
          current: 0,
          total: resources.length,
          message: `Failed to process ${resourceType}: ${error.message}`
        });
      }

      // Log error
      if (onLog) {
        onLog(resourceType, 'ALL', IMPORT_ACTION.ERROR, {
          error: error.message
        });
      }
    }
  }

  return results;
};

/**
 * Process a specific resource type
 */
const processResourceType = async ({
  resourceType,
  resources,
  hierarchyStructure,
  conflictResolution,
  perResourceConflicts = {},
  existingResources,
  nameToIdMaps,
  credentials,
  region,
  instanceId,
  onProgress,
  onLog
}) => {
  const results = {
    success: [],
    failed: [],
    skipped: []
  };

  // Special handling for user hierarchies (must be created in order)
  if (resourceType === 'userHierarchies') {
    const hierarchyResults = await processUserHierarchies({
      resources,
      hierarchyStructure,
      conflictResolution,
      existingResources,
      nameToIdMaps,
      credentials,
      region,
      instanceId,
      onProgress,
      onLog
    });

    return hierarchyResults;
  }

  // Track consecutive circuit breaker errors
  let circuitBreakerAttempts = 0;

  // Process each resource
  for (let i = 0; i < resources.length; i++) {
    const resource = resources[i];
    const resourceName = resource.name;

    // Report progress
    if (onProgress) {
      onProgress({
        resourceType,
        state: RESOURCE_STATE.PROCESSING,
        current: i + 1,
        total: resources.length,
        message: `Processing ${resourceName}...`
      });
    }

    try {
      // Check if resource exists
      const exists = checkResourceExists(resourceType, resourceName, existingResources);

      if (exists) {
        // Get conflict resolution for this specific resource (per-resource override or global)
        const resourceConflictResolution =
          perResourceConflicts[resourceType]?.[resourceName] || conflictResolution;

        // Handle conflict
        if (resourceConflictResolution === CONFLICT_RESOLUTION.SKIP) {
          // Skip existing resource
          results.skipped.push({ resourceType, resourceName });

          if (onLog) {
            onLog(resourceType, resourceName, IMPORT_ACTION.SKIP, {
              reason: 'Resource already exists'
            });
          }

          // Still add to map for dependency resolution
          const existingId = getExistingResourceId(resourceType, resourceName, existingResources);
          if (existingId) {
            updateNameToIdMaps(resourceType, resourceName, existingId, nameToIdMaps);
          }

          continue;
        } else {
          // Update existing resource
          try {
            const existingId = getExistingResourceId(resourceType, resourceName, existingResources);

            if (!existingId) {
              throw new Error('Existing resource ID not found');
            }

            await updateResource({
              resourceType,
              resource,
              existingId,
              nameToIdMaps,
              credentials,
              region,
              instanceId
            });

            // Update map
            updateNameToIdMaps(resourceType, resourceName, existingId, nameToIdMaps);

            results.success.push({ resourceType, resourceName, id: existingId });

            if (onLog) {
              onLog(resourceType, resourceName, IMPORT_ACTION.UPDATE, {
                id: existingId
              });
            }

            // Reset circuit breaker counter on success
            circuitBreakerAttempts = 0;

            continue;
          } catch (error) {
            // Check if this is a circuit breaker error
            if (requestQueueManager.isCircuitBreakerError(error)) {
              circuitBreakerAttempts++;
              console.warn(`🚫 Circuit breaker error updating ${resourceType} "${resourceName}" (attempt ${circuitBreakerAttempts}/${MAX_CIRCUIT_BREAKER_ATTEMPTS})`);

              // Check if we've exceeded max attempts
              if (circuitBreakerAttempts >= MAX_CIRCUIT_BREAKER_ATTEMPTS) {
                console.error(`🛑 Circuit breaker limit reached for ${resourceType}. Skipping remaining ${resources.length - i} resources.`);

                // Log failures for remaining resources (including current one)
                for (let j = i; j < resources.length; j++) {
                  const skippedResource = resources[j];
                  const skippedName = skippedResource.name;

                  results.failed.push({
                    resourceType,
                    resourceName: skippedName,
                    error: 'Skipped due to circuit breaker limit'
                  });

                  if (onLog) {
                    onLog(resourceType, skippedName, IMPORT_ACTION.ERROR, {
                      error: 'Skipped due to circuit breaker limit - too many rate limit errors'
                    });
                  }
                }

                // Report progress - aborted
                if (onProgress) {
                  onProgress({
                    resourceType,
                    state: RESOURCE_STATE.FAILED,
                    current: i,
                    total: resources.length,
                    message: `Aborted ${resourceType} import due to circuit breaker limit`
                  });
                }

                return results;
              }
            }

            // If update fails, log and skip
            console.error(`Error updating ${resourceType} "${resourceName}":`, error);

            results.failed.push({ resourceType, resourceName, error: error.message });

            if (onLog) {
              onLog(resourceType, resourceName, IMPORT_ACTION.ERROR, {
                error: `Update failed: ${error.message}`
              });
            }

            continue;
          }
        }
      }

      // Create new resource
      const createResult = await createResource({
        resourceType,
        resource,
        nameToIdMaps,
        credentials,
        region,
        instanceId
      });

      // Update nameToIdMaps with newly created resource
      updateNameToIdMaps(resourceType, resourceName, createResult.id, nameToIdMaps);

      results.success.push({ resourceType, resourceName, id: createResult.id });

      if (onLog) {
        onLog(resourceType, resourceName, IMPORT_ACTION.CREATE, {
          id: createResult.id,
          arn: createResult.arn
        });
      }

      // Reset circuit breaker counter on success
      circuitBreakerAttempts = 0;

    } catch (error) {
      // Check if this is a circuit breaker error
      if (requestQueueManager.isCircuitBreakerError(error)) {
        circuitBreakerAttempts++;
        console.warn(`🚫 Circuit breaker error creating ${resourceType} "${resourceName}" (attempt ${circuitBreakerAttempts}/${MAX_CIRCUIT_BREAKER_ATTEMPTS})`);

        // Check if we've exceeded max attempts
        if (circuitBreakerAttempts >= MAX_CIRCUIT_BREAKER_ATTEMPTS) {
          console.error(`🛑 Circuit breaker limit reached for ${resourceType}. Skipping remaining ${resources.length - i} resources.`);

          // Log failures for remaining resources (including current one)
          for (let j = i; j < resources.length; j++) {
            const skippedResource = resources[j];
            const skippedName = skippedResource.name;

            results.failed.push({
              resourceType,
              resourceName: skippedName,
              error: 'Skipped due to circuit breaker limit'
            });

            if (onLog) {
              onLog(resourceType, skippedName, IMPORT_ACTION.ERROR, {
                error: 'Skipped due to circuit breaker limit - too many rate limit errors'
              });
            }
          }

          // Report progress - aborted
          if (onProgress) {
            onProgress({
              resourceType,
              state: RESOURCE_STATE.FAILED,
              current: i,
              total: resources.length,
              message: `Aborted ${resourceType} import due to circuit breaker limit`
            });
          }

          return results;
        }

        // Under the limit: the breaker tripped on THIS resource. executeRequest() waits for the
        // breaker to reset before its next attempt, so RETRY the same resource instead of dropping it.
        // Connect write APIs (CreateQueue / CreateRoutingProfile) throttle hard; backoff + retry is what
        // lets them eventually land. A later success resets circuitBreakerAttempts to 0 (see above).
        console.warn(`⏳ Retrying ${resourceType} "${resourceName}" after circuit-breaker backoff...`);
        if (onProgress) {
          onProgress({
            resourceType,
            state: RESOURCE_STATE.PROCESSING,
            current: i,
            total: resources.length,
            message: `Throttled — backing off, then retrying ${resourceName}...`
          });
        }
        i--; // re-process resources[i] on the next loop iteration
        continue;
      }

      console.error(`Error creating ${resourceType} "${resourceName}":`, error);

      // A resource-count service quota (e.g. max queues per instance) is PERMANENT — surface a clear,
      // actionable message instead of the raw "LimitExceededException" so the engineer knows to delete
      // unused resources or request a quota increase rather than just retrying.
      const isQuotaError = error.name === 'LimitExceededException' || error.name === 'ServiceQuotaExceededException';
      const friendlyError = isQuotaError
        ? `Service quota reached for ${resourceType}: the Connect instance is at its maximum number of ${resourceType}. Delete unused ${resourceType} or request a quota increase, then re-run. (${error.message})`
        : error.message;

      results.failed.push({ resourceType, resourceName, error: friendlyError });

      if (onLog) {
        onLog(resourceType, resourceName, IMPORT_ACTION.ERROR, {
          error: friendlyError
        });
      }

      // Continue with next resource (don't stop entire import on single failure)
    }
  }

  return results;
};;

/**
 * Build hierarchy structure params from Excel data for AWS API
 */
const buildHierarchyStructureParams = (excelStructure) => {
  if (!excelStructure) return null;

  const params = {};

  if (excelStructure.levelOne && excelStructure.levelOne !== 'N/A') {
    params.LevelOne = { Name: excelStructure.levelOne };
  }
  if (excelStructure.levelTwo && excelStructure.levelTwo !== 'N/A') {
    params.LevelTwo = { Name: excelStructure.levelTwo };
  }
  if (excelStructure.levelThree && excelStructure.levelThree !== 'N/A') {
    params.LevelThree = { Name: excelStructure.levelThree };
  }
  if (excelStructure.levelFour && excelStructure.levelFour !== 'N/A') {
    params.LevelFour = { Name: excelStructure.levelFour };
  }
  if (excelStructure.levelFive && excelStructure.levelFive !== 'N/A') {
    params.LevelFive = { Name: excelStructure.levelFive };
  }

  // Return null if no levels defined
  return Object.keys(params).length > 0 ? params : null;
};

/**
 * Process user hierarchies (special handling for parent→child order)
 */
const processUserHierarchies = async ({
  resources,
  hierarchyStructure,
  conflictResolution,
  existingResources,
  nameToIdMaps,
  credentials,
  region,
  instanceId,
  onProgress,
  onLog
}) => {
  const results = {
    success: [],
    failed: [],
    skipped: []
  };

  // Track consecutive circuit breaker errors
  let circuitBreakerAttempts = 0;

  // STEP 1: Update hierarchy structure (level names) first
  // This must be done before creating groups
  if (hierarchyStructure) {
    const structureParams = buildHierarchyStructureParams(hierarchyStructure);

    if (structureParams) {
      try {
        if (onLog) {
          onLog('userHierarchies', 'STRUCTURE', IMPORT_ACTION.CREATE, {
            message: 'Updating hierarchy structure (level names)...',
            levels: hierarchyStructure
          });
        }

        await updateUserHierarchyStructure(credentials, region, instanceId, structureParams);

        if (onLog) {
          onLog('userHierarchies', 'STRUCTURE', IMPORT_ACTION.CREATE, {
            message: 'Hierarchy structure updated successfully'
          });
        }
      } catch (error) {
        console.error('Error updating hierarchy structure:', error);

        // Check for circuit breaker error on structure update
        if (requestQueueManager.isCircuitBreakerError(error)) {
          circuitBreakerAttempts++;
          console.warn(`🚫 Circuit breaker error updating hierarchy structure (attempt ${circuitBreakerAttempts}/${MAX_CIRCUIT_BREAKER_ATTEMPTS})`);
        }

        if (onLog) {
          onLog('userHierarchies', 'STRUCTURE', IMPORT_ACTION.ERROR, {
            error: `Failed to update hierarchy structure: ${error.message}`
          });
        }
        // Continue with group creation - structure might already exist
      }
    }
  }

  // STEP 2: Process groups
  // Check if data is already transformed (has 'name' and 'level' properties)
  // The UI transforms the data for display, so it may already be in hierarchical format
  let hierarchyGroups;
  if (resources.length > 0 && resources[0].name !== undefined && resources[0].level !== undefined) {
    // Data is already transformed, just sort by level
    hierarchyGroups = [...resources].sort((a, b) => a.level - b.level);
  } else {
    // Data is in raw Excel format, transform it
    hierarchyGroups = buildUserHierarchyStructure(resources);
  }

  // Helper to build a full path key from row data
  // This handles duplicate names at different levels (e.g., "Automotive" at Level 1 and Level 2)
  const buildFullPathKey = (row, level) => {
    const parts = [];
    for (let i = 1; i <= level; i++) {
      const levelKey = `level${i}`;
      const value = row[levelKey];
      if (value && value !== 'N/A' && value !== '') {
        parts.push(value);
      }
    }
    return parts.join('|');
  };

  // Helper to build parent's full path key
  const buildParentPathKey = (row, level) => {
    if (level <= 1) return null;
    return buildFullPathKey(row, level - 1);
  };

  // Process each hierarchy group in order
  for (let i = 0; i < hierarchyGroups.length; i++) {
    const group = hierarchyGroups[i];
    const groupName = group.name;

    // Build full path key for this group (handles duplicate names at different levels)
    const fullPathKey = group.row ? buildFullPathKey(group.row, group.level) : group.uniqueKey || groupName;
    const parentPathKey = group.row ? buildParentPathKey(group.row, group.level) : (group.level > 1 ? group.parentName : null);

    // Report progress
    if (onProgress) {
      onProgress({
        resourceType: 'userHierarchies',
        state: RESOURCE_STATE.PROCESSING,
        current: i + 1,
        total: hierarchyGroups.length,
        message: `Processing hierarchy group "${groupName}" (Level ${group.level})...`
      });
    }

    try {
      // Check if group exists using the FULL PATH KEY (handles duplicate names at different levels)
      // existingResources.userHierarchyGroups is now keyed by full path (e.g., "Automotive|Automotive")
      const existingGroup = existingResources.userHierarchyGroups[fullPathKey];
      const exists = !!existingGroup;

      if (exists) {
        if (conflictResolution === CONFLICT_RESOLUTION.SKIP) {
          results.skipped.push({ resourceType: 'userHierarchies', resourceName: groupName });

          if (onLog) {
            onLog('userHierarchies', groupName, IMPORT_ACTION.SKIP, {
              reason: 'Hierarchy group already exists',
              existingId: existingGroup.id,
              fullPath: fullPathKey
            });
          }

          // Add to map for child references using full path key
          nameToIdMaps.hierarchyGroupNameToId[fullPathKey] = existingGroup.id;

          continue;
        }
      }

      // Create hierarchy group
      const createData = {
        Name: groupName,
        Tags: {}
      };

      // Add parent group ID if not level 1 - use parent's full path key for lookup
      if (parentPathKey && nameToIdMaps.hierarchyGroupNameToId[parentPathKey]) {
        createData.ParentGroupId = nameToIdMaps.hierarchyGroupNameToId[parentPathKey];
      }

      const createResult = await createUserHierarchyGroup(
        credentials,
        region,
        instanceId,
        createData
      );

      // Update map with newly created group using full path key
      nameToIdMaps.hierarchyGroupNameToId[fullPathKey] = createResult.id;

      results.success.push({ resourceType: 'userHierarchies', resourceName: groupName, id: createResult.id });

      if (onLog) {
        onLog('userHierarchies', groupName, IMPORT_ACTION.CREATE, {
          id: createResult.id,
          arn: createResult.arn,
          level: group.level
        });
      }

      // Reset circuit breaker counter on success
      circuitBreakerAttempts = 0;

    } catch (error) {
      // Check if this is a circuit breaker error
      if (requestQueueManager.isCircuitBreakerError(error)) {
        circuitBreakerAttempts++;
        console.warn(`🚫 Circuit breaker error creating hierarchy group "${groupName}" (attempt ${circuitBreakerAttempts}/${MAX_CIRCUIT_BREAKER_ATTEMPTS})`);

        // Check if we've exceeded max attempts
        if (circuitBreakerAttempts >= MAX_CIRCUIT_BREAKER_ATTEMPTS) {
          console.error(`🛑 Circuit breaker limit reached for userHierarchies. Skipping remaining ${hierarchyGroups.length - i} groups.`);

          // Log failures for remaining groups (including current one)
          for (let j = i; j < hierarchyGroups.length; j++) {
            const skippedGroup = hierarchyGroups[j];
            const skippedName = skippedGroup.name;

            results.failed.push({
              resourceType: 'userHierarchies',
              resourceName: skippedName,
              error: 'Skipped due to circuit breaker limit'
            });

            if (onLog) {
              onLog('userHierarchies', skippedName, IMPORT_ACTION.ERROR, {
                error: 'Skipped due to circuit breaker limit - too many rate limit errors',
                level: skippedGroup.level
              });
            }
          }

          // Report progress - aborted
          if (onProgress) {
            onProgress({
              resourceType: 'userHierarchies',
              state: RESOURCE_STATE.FAILED,
              current: i,
              total: hierarchyGroups.length,
              message: 'Aborted userHierarchies import due to circuit breaker limit'
            });
          }

          return results;
        }
      }

      console.error(`Error creating hierarchy group "${groupName}":`, error);

      results.failed.push({ resourceType: 'userHierarchies', resourceName: groupName, error: error.message });

      if (onLog) {
        onLog('userHierarchies', groupName, IMPORT_ACTION.ERROR, {
          error: error.message,
          level: group.level
        });
      }
    }
  }

  return results;
};;

/**
 * Create a resource based on type
 */
const createResource = async ({
  resourceType,
  resource,
  nameToIdMaps,
  credentials,
  region,
  instanceId
}) => {
  switch (resourceType) {
    case 'hoursOfOperation':
      const hoursData = mapHoursOfOperationForCreate(resource);
      return await createHoursOfOperation(credentials, region, instanceId, hoursData);

    case 'agentStatus':
      const agentStatusData = mapAgentStatusForCreate(resource);
      return await createAgentStatus(credentials, region, instanceId, agentStatusData);

    case 'queues':
      const queueData = mapQueueForCreate(resource, nameToIdMaps);
      return await createQueue(credentials, region, instanceId, queueData);

    case 'quickConnects':
      const quickConnectData = mapQuickConnectForCreate(resource, nameToIdMaps);
      return await createQuickConnect(credentials, region, instanceId, quickConnectData);

    case 'routingProfiles':
      const routingProfileData = mapRoutingProfileForCreate(resource, nameToIdMaps);
      return await createRoutingProfile(credentials, region, instanceId, routingProfileData);

    default:
      throw new Error(`Unknown resource type: ${resourceType}`);
  }
};

/**
 * Update an existing resource
 */
const updateResource = async ({
  resourceType,
  resource,
  existingId,
  nameToIdMaps,
  credentials,
  region,
  instanceId
}) => {
  switch (resourceType) {
    case 'hoursOfOperation':
      const hoursDataForUpdate = mapHoursOfOperationForCreate(resource);
      return await updateHoursOfOperation(credentials, region, instanceId, existingId, hoursDataForUpdate);

    case 'agentStatus':
      const agentStatusData = mapAgentStatusForCreate(resource);
      return await updateAgentStatus(credentials, region, instanceId, existingId, agentStatusData);

    case 'queues':
      const queueData = mapQueueForCreate(resource, nameToIdMaps);
      // Only update outbound caller config (Hours of Operation cannot be changed)
      if (queueData.OutboundCallerConfig) {
        return await updateQueueOutboundCallerConfig(credentials, region, instanceId, existingId, queueData.OutboundCallerConfig);
      }
      return { success: true };

    case 'quickConnects':
      const quickConnectData = mapQuickConnectForCreate(resource, nameToIdMaps);
      return await updateQuickConnectConfig(credentials, region, instanceId, existingId, quickConnectData);

    case 'routingProfiles':
      const routingProfileData = mapRoutingProfileForCreate(resource, nameToIdMaps);
      // Update queue configs
      if (routingProfileData.QueueConfigs) {
        return await updateRoutingProfileQueues(credentials, region, instanceId, existingId, routingProfileData.QueueConfigs);
      }
      return { success: true };

    default:
      throw new Error(`Update not supported for resource type: ${resourceType}`);
  }
};

/**
 * Check if a resource exists in the instance
 */
const checkResourceExists = (resourceType, resourceName, existingResources) => {
  const resourceMap = getResourceMap(resourceType, existingResources);
  return resourceName in resourceMap;
};

/**
 * Get existing resource ID
 */
const getExistingResourceId = (resourceType, resourceName, existingResources) => {
  const resourceMap = getResourceMap(resourceType, existingResources);
  return resourceMap[resourceName]?.id;
};

/**
 * Get resource map for a given type
 */
const getResourceMap = (resourceType, existingResources) => {
  const mapKey = {
    hoursOfOperation: 'hoursOfOperation',
    agentStatus: 'agentStatus',
    queues: 'queues',
    quickConnects: 'quickConnects',
    routingProfiles: 'routingProfiles',
    userHierarchies: 'userHierarchyGroups'
  }[resourceType];

  return existingResources[mapKey] || {};
};

/**
 * Update nameToIdMaps with newly created resource
 */
const updateNameToIdMaps = (resourceType, resourceName, resourceId, nameToIdMaps) => {
  switch (resourceType) {
    case 'hoursOfOperation':
      nameToIdMaps.hoursOfOperationNameToId[resourceName] = resourceId;
      break;
    case 'agentStatus':
      nameToIdMaps.agentStatusNameToId[resourceName] = resourceId;
      break;
    case 'queues':
      nameToIdMaps.queueNameToId[resourceName] = resourceId;
      break;
    case 'quickConnects':
      nameToIdMaps.quickConnectNameToId[resourceName] = resourceId;
      break;
    case 'routingProfiles':
      nameToIdMaps.routingProfileNameToId[resourceName] = resourceId;
      break;
    case 'userHierarchies':
      nameToIdMaps.hierarchyGroupNameToId[resourceName] = resourceId;
      break;
    default:
      break;
  }
};
